import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, ArrowRight, Check, X, Car, Calendar, Hammer, Banknote, UsersRound, Bell, LineChart,
  Gauge, Users, ShieldCheck, Package, FileText, Receipt, CreditCard, FolderOpen, CalendarCheck,
  UserCheck, FileCheck2, PackageSearch,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { savePendingSelection } from "@/lib/billing";
import { Footer } from "@/components/Footer";
import {
  DashboardPreview, CalendarPreview, MotPreview, QuotePreview, InvoicePreview,
  FinancePreview, CustomersPreview, PartsPreview, TeamPreview, NotificationsPreview, JobPreview,
} from "@/components/LandingPreviews";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";

const MODULES = [
  { icon: Gauge, title: "Dashboard", body: "A live snapshot of revenue, outstanding balances, active jobs and upcoming bookings." },
  { icon: CalendarCheck, title: "Bookings & Calendar", body: "A workshop diary with online booking requests you approve and confirm." },
  { icon: Users, title: "Customers & Vehicles", body: "Every customer and their vehicles, history and documents kept together." },
  { icon: ShieldCheck, title: "DVLA & MOT", body: "Look up a registration and pull vehicle details and MOT history." },
  { icon: FileText, title: "Quotes & Estimates", body: "Build quotations, email them and track customer approval." },
  { icon: Hammer, title: "Job Management", body: "Move work through clear statuses from open to invoiced." },
  { icon: Receipt, title: "Invoices & Payments", body: "Branded invoices, recorded payments and outstanding balances." },
  { icon: LineChart, title: "Finance & Reporting", body: "Revenue, expenses and net profit with PDF / CSV exports." },
  { icon: Package, title: "Parts & Suppliers", body: "Track parts, stock levels and the suppliers you buy from." },
  { icon: UsersRound, title: "Team Management", body: "Add team members and control what each person can access." },
  { icon: Bell, title: "Customer Notifications", body: "Booking, quote and invoice emails keep customers in the loop." },
];

const WORKFLOW = [
  { icon: CalendarCheck, title: "Booking comes in", body: "A customer sends an online booking request, or you record an appointment in the diary.", tag: "Customer or garage" },
  { icon: Car, title: "Check the vehicle", body: "Pull up the customer and vehicle, with a DVLA lookup and MOT history to hand.", tag: "Manual lookup" },
  { icon: FileCheck2, title: "Prepare a quote", body: "Build an estimate where one is needed and email it for the customer to approve.", tag: "Manual · emailed" },
  { icon: Hammer, title: "Run the job", body: "Record the job, schedule it and move it through its statuses as work progresses.", tag: "Manual" },
  { icon: UserCheck, title: "Approve extra work", body: "Send additional-work approvals to the customer before you carry them out.", tag: "Where supported" },
  { icon: CreditCard, title: "Invoice & take payment", body: "Create the invoice, record full or part payments and watch the balance update.", tag: "Manual" },
  { icon: FolderOpen, title: "Keep the history", body: "The vehicle and job history stays on file for the next visit.", tag: "Automatic" },
];

const SECTIONS = [
  {
    id: "dashboard", eyebrow: "DASHBOARD", title: "Your whole workshop at a glance", Preview: DashboardPreview,
    desc: "Open Pro Garages and see where the day stands — money in, money owed, jobs on the go and what is booked in next.",
    bullets: ["Revenue, outstanding balances and vehicle counts up top", "Active jobs with their current status", "Upcoming bookings for the days ahead", "Quick actions to start a job, invoice or lookup"],
    howto: "A workshop owner starts the morning here to see what needs chasing and which jobs are in progress.",
  },
  {
    id: "bookings", eyebrow: "BOOKINGS & CALENDAR", title: "A diary that fills itself", Preview: CalendarPreview, reverse: true,
    desc: "Manage your workshop diary in one place and let customers send booking requests through your own online booking page.",
    bullets: ["Online booking requests you approve, confirm or decline", "Week and day views of what is booked in", "MOT appointment bookings handled in the same flow", "Email confirmations for bookings, changes and cancellations"],
    howto: "A garage shares its booking link so customers can request a slot, then confirms the ones that suit the diary.",
  },
  {
    id: "customers", eyebrow: "CUSTOMERS & VEHICLES", title: "Every customer and vehicle on file", Preview: CustomersPreview,
    desc: "Keep customer contact details and their vehicles together, so the full history is a click away on the next visit.",
    bullets: ["Customer records linked to their vehicles", "Vehicle details, history and documents in one place", "Find a customer or registration in seconds", "Records connect through to jobs, quotes and invoices"],
    howto: "When a regular rings up, the front desk pulls their record and sees every past job on their vehicles.",
  },
  {
    id: "dvla", eyebrow: "DVLA & MOT INFORMATION", title: "Look up a reg, see the vehicle", Preview: MotPreview, reverse: true,
    desc: "Enter a registration to retrieve vehicle details and MOT history through the supported lookup, ready to attach to a job.",
    bullets: ["Vehicle registration (VRM) lookup via DVLA", "MOT history with test results and advisories", "Details you can save against the customer and vehicle", "Fewer re-keyed number plates and typos"],
    howto: "Before a service, a technician checks the reg to confirm the vehicle and review previous MOT advisories.",
    note: "Vehicle and MOT data is retrieved from third-party providers, so availability depends on those services.",
  },
  {
    id: "quotes", eyebrow: "QUOTES & ESTIMATES", title: "Quote fast, get the go-ahead", Preview: QuotePreview,
    desc: "Put together a clear estimate, email it to the customer and track whether it has been approved.",
    bullets: ["Build quotations with parts and labour lines", "Email quotes straight to the customer", "Online customer approval where enabled", "Carry an approved quote through to the job"],
    howto: "A garage sends a brake quote by email and sees when the customer approves it online before ordering parts.",
  },
  {
    id: "jobs", eyebrow: "JOB MANAGEMENT", title: "Track work from start to finish", Preview: JobPreview, reverse: true,
    desc: "Record each repair as a job and move it through clear statuses so nothing slips through the cracks.",
    bullets: ["A clear status workflow from open to invoiced", "Assign jobs to technicians where supported", "Attach damage photos and videos to the job", "Check-in and handover records on the vehicle"],
    howto: "The workshop sees at a glance which jobs are in progress, waiting on parts or ready to invoice.",
  },
  {
    id: "invoices", eyebrow: "INVOICES & PAYMENTS", title: "Invoice and get paid", Preview: InvoicePreview,
    desc: "Turn finished work into a branded invoice, record how the customer paid and keep an eye on what is still owed.",
    bullets: ["Branded invoices with your logo and prefix", "Record full, partial or overpayments", "Outstanding balances tracked automatically", "Email invoices and receipts to customers"],
    howto: "When a job is done the garage raises the invoice, takes a card payment and the balance updates on the spot.",
  },
  {
    id: "finance", eyebrow: "FINANCE & REPORTING", title: "Know how the business is doing", Preview: FinancePreview, reverse: true,
    desc: "See revenue, expenses and net profit over the period you choose, and export the figures for your accountant.",
    bullets: ["Revenue, outstanding, expenses and net profit", "Expenses linked to suppliers, jobs and vehicles", "Reports across 7 days, 30 days, a year or all time", "Accounting exports to PDF and CSV"],
    howto: "At month end the owner reviews profit, checks outstanding invoices and exports the numbers for the books.",
  },
  {
    id: "parts", eyebrow: "PARTS & SUPPLIERS", title: "Keep the right parts on the shelf", Preview: PartsPreview,
    desc: "Track the parts you hold, see stock levels and keep the suppliers you buy from in one tidy list.",
    bullets: ["Parts inventory with stock levels", "Suppliers and purchasing in one place", "Spot what is running low at a glance", "Link parts through to jobs and expenses"],
    howto: "A garage checks stock before a job and reorders from the right supplier when a part is running low.",
    note: "Parts, inventory and supplier tools are available on the Pro and Premium plans.",
  },
  {
    id: "team", eyebrow: "TEAM MANAGEMENT", title: "The right access for each person", Preview: TeamPreview, reverse: true,
    desc: "Add your team and control what each person can see and do, so the workshop runs with the right permissions.",
    bullets: ["Add team members to the account", "Role-based access to features", "Assign work to the right technician", "Records stay available by account permission"],
    howto: "The owner gives technicians access to jobs while keeping billing and settings to themselves.",
    note: "Team member limits depend on your plan — see pricing below.",
  },
  {
    id: "notifications", eyebrow: "CUSTOMER NOTIFICATIONS", title: "Keep customers in the loop by email", Preview: NotificationsPreview,
    desc: "Pro Garages sends the emails that matter — so customers know their booking is confirmed and their invoice is ready.",
    bullets: ["Booking confirmations, changes and cancellations", "Quotations, invoices and receipts by email", "Overdue invoice reminders", "Email is the supported notification channel"],
    howto: "A customer books online and receives a confirmation email, then a receipt once they have paid.",
    note: "Notifications are sent by email. SMS and messaging apps are not part of the current notification system.",
  },
];

const BENEFITS = [
  { icon: CalendarCheck, title: "Bookings and jobs, organised", body: "One diary and one job list, instead of a wall planner and a notebook." },
  { icon: FolderOpen, title: "Less scattered paperwork", body: "Customer, vehicle, quote, job and invoice details live together, not across sticky notes." },
  { icon: Car, title: "Records that stay together", body: "Every vehicle keeps its own history so you are not starting from scratch each visit." },
  { icon: FileCheck2, title: "Faster quotes and invoices", body: "Build an estimate, email it, and turn approved work into an invoice without re-typing it." },
  { icon: CreditCard, title: "See what you are owed", body: "Payments and outstanding balances are tracked so overdue invoices do not get forgotten." },
  { icon: Bell, title: "Customers kept informed", body: "Booking and invoice emails mean fewer phone calls asking what is happening." },
];

const AUDIENCE = [
  { icon: Wrench, title: "Independent garages", body: "Run the full workshop — bookings, jobs, quotes, invoices and finance — from one system." },
  { icon: Users, title: "Small workshop teams", body: "Give each technician the right access and keep everyone working from the same records." },
  { icon: UserCheck, title: "Sole-trader mechanics", body: "A tidy way to manage customers, vehicles and invoices without the paperwork piling up." },
  { icon: PackageSearch, title: "Mobile mechanics", body: "Record jobs, look up vehicles and raise invoices on your phone between visits." },
];

const FAQS = [
  { q: "What is Pro Garages?", a: "Pro Garages is online garage management software for UK independent garages, small workshop teams and mobile mechanics. It brings bookings, customers, vehicles, jobs, quotes, invoices, payments, parts and finance together in one system you can use on a laptop or phone." },
  { q: "Who is it designed for?", a: "Independent garages, small workshop teams, sole-trader mechanics and mobile mechanics who want to manage their everyday operations in one place rather than across paper and spreadsheets." },
  { q: "What can I manage in the system?", a: "A workshop diary and bookings, customer and vehicle records, DVLA and MOT lookups, quotes and estimates, jobs and their statuses, invoices and payments, parts and suppliers, team access, and finance reporting with PDF and CSV exports." },
  { q: "Can customers make bookings online?", a: "Yes. You can share your own online booking page where customers send booking requests. You review, approve, confirm or decline them, and confirmation emails are sent for bookings, changes and cancellations." },
  { q: "How do vehicle lookups and MOT information work?", a: "You enter a vehicle registration and Pro Garages retrieves vehicle details and MOT history through a supported third-party lookup, which you can save against the customer and vehicle. Availability of that data depends on the third-party provider." },
  { q: "How does the 14-day free trial work?", a: "Eligible new customers can start a 14-day free trial. Payment details are required through the Stripe checkout when you start, as the checkout requires them. You can cancel anytime, subject to the applicable cancellation terms. Customers who have already used a trial are not automatically entitled to another one." },
  { q: "What happens when a subscription is cancelled?", a: "When a cancellation takes effect the account may move to read-only access, where you can still view existing records but can no longer create or edit them or use subscription features. Full details are in the Subscription & Billing Policy linked in the footer." },
  { q: "How can I contact support?", a: "Email support@progarages.co.uk and include your account email and any relevant details so the team can help quickly." },
];

const ANNUAL_MONTHS = 10;
const COMPARE = [
  { label: "New jobs per month", basic: "50", pro: "Unlimited", premium: "Unlimited" },
  { label: "Team members", basic: "1", pro: "Up to 3", premium: "Unlimited" },
  { label: "Bookings & calendar", basic: true, pro: true, premium: true },
  { label: "Online booking requests", basic: true, pro: true, premium: true },
  { label: "Customers & vehicles", basic: true, pro: true, premium: true },
  { label: "DVLA lookup & MOT history", basic: true, pro: true, premium: true },
  { label: "Quotes, invoices & payments", basic: true, pro: true, premium: true },
  { label: "Finance dashboard & expenses", basic: true, pro: true, premium: true },
  { label: "Email customer notifications", basic: true, pro: true, premium: true },
  { label: "Damage photos & videos", basic: false, pro: true, premium: true },
  { label: "Parts & inventory", basic: false, pro: true, premium: true },
  { label: "Suppliers & purchasing", basic: false, pro: true, premium: true },
  { label: "Branded invoices (logo & prefix)", basic: false, pro: true, premium: true },
  { label: "Accounting exports (PDF / CSV)", basic: false, pro: true, premium: true },
  { label: "Payment reminders & review links", basic: false, pro: true, premium: true },
  { label: "Advanced financial & profit reports", basic: false, pro: false, premium: true },
  { label: "Enhanced team permissions", basic: false, pro: false, premium: true },
  { label: "Salary & payroll tracking", basic: false, pro: false, premium: true },
  { label: "Priority support", basic: false, pro: false, premium: true },
];
const TIERS = [
  { id: "basic", name: "BASIC", price: 40, tagline: "For solo mechanics and smaller workshops", includes: ["Up to 50 new jobs / month", "Vehicles, customers & balances", "Quotations, invoices & payments", "Bookings & calendar", "DVLA lookup + MOT history", "Finance dashboard & expenses", "1 user"], excludes: ["Damage photos & videos", "Parts & inventory", "Suppliers & purchasing", "Extra team members", "Accounting exports"] },
  { id: "pro", name: "PRO", price: 50, tagline: "For growing independent garages", includes: ["Everything in Basic", "Unlimited jobs & vehicles", "Up to 3 team members", "Damage photos + videos", "Full parts & inventory", "Suppliers & purchasing", "Branded invoices with logo", "Accounting exports (PDF/CSV)", "Payment reminders + review links"], excludes: ["Advanced profit reports", "Priority support"], highlight: true },
  { id: "premium", name: "PREMIUM", price: 60, tagline: "For larger or multi-technician workshops", includes: ["Everything in Pro", "Unlimited team members", "Advanced financial & profit reports", "Enhanced team permissions", "Salary & payroll tracking", "Priority support"], excludes: [] },
];

function Eyebrow({ children }) {
  return <p className="mb-2 text-[11px] font-extrabold tracking-[0.2em] text-brand">{children}</p>;
}

function CompareCell({ v, highlight }) {
  return (
    <td className={`px-4 py-3 text-center ${highlight ? "bg-brand-tertiary" : ""}`}>
      {v === true ? (
        <Check className="mx-auto h-4 w-4 text-success" aria-label="Included" />
      ) : v === false ? (
        <span className="text-slate-300" aria-label="Not included">—</span>
      ) : (
        <span className="text-xs font-bold text-midnight">{v}</span>
      )}
    </td>
  );
}

function FeatureSection({ s }) {
  const Preview = s.Preview;
  return (
    <div id={s.id} className="grid items-center gap-10 py-14 lg:grid-cols-2 lg:gap-16">
      <Reveal from={s.reverse ? "right" : "left"} className={s.reverse ? "lg:order-2" : ""}>
        <Eyebrow>{s.eyebrow}</Eyebrow>
        <h3 className="font-display text-3xl font-bold text-midnight sm:text-4xl">{s.title}</h3>
        <p className="mt-3 text-base leading-relaxed text-slate-600">{s.desc}</p>
        <ul className="mt-5 space-y-2.5">
          {s.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{b}</li>
          ))}
        </ul>
        <div className="mt-5 rounded-xl border border-border bg-card p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-brand-2">How a garage uses it</p>
          <p className="mt-1 text-sm text-slate-600">{s.howto}</p>
        </div>
        {s.note && <p className="mt-3 text-xs italic text-muted-foreground">{s.note}</p>}
      </Reveal>
      <Reveal from={s.reverse ? "left" : "right"} delay={120} className={s.reverse ? "lg:order-1" : ""}><Preview /></Reveal>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [period, setPeriod] = useState("monthly");

  useEffect(() => { if (!loading && user) navigate("/dashboard"); }, [user, loading, navigate]);

  const pickTier = (tid) => {
    savePendingSelection(tid, period);
    if (user) navigate("/billing");
    else navigate(`/login?tier=${tid}&period=${period}&mode=register`);
  };
  const startTrial = () => navigate("/login?mode=register");

  return (
    <div className="min-h-screen bg-background" data-testid="landing-screen">
      {/* Nav */}
      <header className="sticky top-0 z-40 flex items-center justify-between bg-midnight px-6 py-3.5 md:px-10">
        <button onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="nav-logo" className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10"><Wrench className="h-4 w-4 text-brand" /></div>
          <span className="font-display text-[15px] font-bold tracking-widest text-white">PRO GARAGES</span>
        </button>
        <nav className="hidden items-center gap-6 md:flex">
          <a href="#features" data-testid="nav-features" className="text-[13px] font-semibold text-white/85 hover:text-white">Features</a>
          <a href="#how-it-works" data-testid="nav-how" className="text-[13px] font-semibold text-white/85 hover:text-white">How it works</a>
          <a href="#pricing" data-testid="nav-pricing" className="text-[13px] font-semibold text-white/85 hover:text-white">Pricing</a>
        </nav>
        <div className="flex items-center gap-5">
          <button onClick={() => navigate("/login")} data-testid="nav-signin" className="text-[13px] font-semibold text-white/85 hover:text-white">Sign in</button>
          <button onClick={startTrial} data-testid="nav-cta" className="rounded-lg bg-brand px-4 py-2 text-xs font-extrabold tracking-wide text-white transition-transform hover:scale-105">Start free trial</button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-midnight to-midnight-2 px-6 py-20 md:px-10">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <div className="text-center lg:text-left">
            <p className="mb-4 text-[11px] font-extrabold tracking-[0.2em] text-white/60">WORKSHOP OS FOR UK GARAGES</p>
            <h1 className="font-display text-5xl font-bold leading-[1.05] text-white sm:text-6xl">
              Run your entire garage <span className="text-brand-2">from one screen.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/75 lg:mx-0">
              From customer bookings and vehicle records to jobs, quotes, invoices and payments, Pro Garages brings your everyday workshop operations together in one simple system — with online bookings, a workshop calendar, DVLA and MOT information and email notifications for your customers.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              <button onClick={startTrial} data-testid="hero-cta" className="inline-flex items-center gap-2 rounded-lg bg-brand px-7 py-3.5 text-sm font-extrabold tracking-wide text-white transition-transform hover:scale-105">
                START YOUR 14-DAY FREE TRIAL <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#features" data-testid="hero-explore" className="rounded-lg border border-white/30 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/5">Explore the features</a>
            </div>
            <p className="mt-4 text-xs text-white/55">14-day free trial · Payment details required · Cancel anytime</p>
          </div>
          <div className="animate-fade-in-up"><DashboardPreview /></div>
        </div>
      </section>

      {/* About */}
      <section className="px-6 pt-20 pb-6 text-center md:px-10">
        <div className="mx-auto max-w-3xl">
          <Eyebrow>ABOUT PRO GARAGES</Eyebrow>
          <h2 className="font-display text-3xl font-bold text-midnight sm:text-4xl">Built by mechanics, for mechanics.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Every feature in Pro Garages is designed around the real challenges independent garage owners face at the workbench. GBP-first, DVLA-focused, and easy enough that your busiest technician can update a job status with oily fingers on a phone.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Our goal is to make everyday garage management simpler — from taking bookings and preparing quotes to managing repairs, tracking payments and understanding how the business is performing.
          </p>
        </div>
      </section>

      {/* System at a glance */}
      <section className="px-6 pt-10 pb-20 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>THE WHOLE SYSTEM</Eyebrow>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-midnight sm:text-4xl">A guided tour of everything inside</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600">Pro Garages is made up of connected modules that cover the whole workshop. Here is what each one is for.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.title} delay={(i % 3) * 80}>
                  <div className="h-full rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-brand">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-brand-tertiary"><Icon className="h-5 w-5 text-brand" /></div>
                    <h3 className="font-display text-lg font-bold uppercase text-midnight">{m.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{m.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works / workflow */}
      <section id="how-it-works" className="border-y border-border bg-card px-6 py-20 md:px-10">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <Eyebrow>HOW IT WORKS</Eyebrow>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-midnight sm:text-4xl">How a job moves through Pro Garages</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600">A typical job, from the first booking to the vehicle history on file. Each step shows whether it is done by hand or happens automatically.</p>
          </div>
          <div className="mt-10 space-y-3">
            {WORKFLOW.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={Math.min(i, 6) * 70}>
                  <div className="flex items-start gap-4 rounded-xl border border-border bg-background p-5">
                    <div className="flex flex-col items-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white"><Icon className="h-5 w-5" /></div>
                      {i < WORKFLOW.length - 1 && <div className="mt-1 h-full min-h-[14px] w-0.5 flex-1 bg-border" />}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-tertiary text-[11px] font-extrabold text-brand-2">{i + 1}</span>
                        <h3 className="font-display text-lg font-bold uppercase text-midnight">{w.title}</h3>
                        <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">{w.tag}</span>
                      </div>
                      <p className="mt-1 text-sm text-slate-600">{w.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-muted-foreground">Pro Garages organises and connects these steps. You stay in control of each action — the software does not carry out repairs, bookings or payments on its own.</p>
        </div>
      </section>

      {/* Detailed feature sections */}
      <section id="features" className="px-6 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="pt-20 text-center">
            <Eyebrow>FEATURES IN DETAIL</Eyebrow>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-midnight sm:text-4xl">See how each part works</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600">A closer look at the main modules, with a preview of the real interface for each. Previews use fictional demonstration data.</p>
          </div>
          <div className="divide-y divide-border">
            {SECTIONS.map((s) => <FeatureSection key={s.id} s={s} />)}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-y border-border bg-card px-6 py-20 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>WHY GARAGES USE IT</Eyebrow>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-midnight sm:text-4xl">The everyday problems it helps with</h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={(i % 3) * 80}>
                  <div className="h-full rounded-xl border border-border bg-background p-6 text-left">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-brand-tertiary"><Icon className="h-5 w-5 text-brand" /></div>
                    <h3 className="font-display text-lg font-bold uppercase text-midnight">{b.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{b.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="px-6 py-20 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <Eyebrow>WHO IS PRO GARAGES FOR?</Eyebrow>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-midnight sm:text-4xl">Built for UK garages of every size</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600">The same system supports different kinds of automotive business in different ways.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCE.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={(i % 4) * 70}>
                  <div className="h-full rounded-xl border border-border bg-card p-6 text-left">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-brand-tertiary"><Icon className="h-5 w-5 text-brand" /></div>
                    <h3 className="font-display text-lg font-bold uppercase text-midnight">{a.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{a.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-y border-border bg-card px-6 py-20 md:px-10" data-testid="pricing-section">
        <div className="mx-auto max-w-6xl text-center">
          <Eyebrow>PRICING</Eyebrow>
          <h2 className="mb-3 font-display text-3xl font-bold text-midnight sm:text-4xl">Simple plans. No setup fees.</h2>
          <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-slate-600">
            Choose the plan that fits your workshop. Start with the tools you need and select a different plan when your business needs change, subject to the available subscription options.
          </p>
          <div className="mx-auto mb-10 inline-flex items-center gap-1 rounded-full border border-border bg-secondary p-1" data-testid="period-toggle">
            {["monthly", "annual"].map((p) => (
              <button key={p} data-testid={`toggle-${p}`} onClick={() => setPeriod(p)}
                className={`flex items-center gap-2 rounded-full px-5 py-2 text-xs font-extrabold tracking-widest transition-colors ${period === p ? "bg-midnight text-white" : "text-slate-500"}`}>
                {p.toUpperCase()}
                {p === "annual" && <span className="rounded-full bg-success px-2 py-0.5 text-[9px] font-extrabold text-white">SAVE 2 MONTHS</span>}
              </button>
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            {TIERS.map((t) => {
              const annualTotal = t.price * ANNUAL_MONTHS;
              const price = period === "annual" ? annualTotal : t.price;
              return (
                <div key={t.id} data-testid={`tier-${t.id}`}
                  className={`relative rounded-2xl border bg-background p-8 text-left ${t.highlight ? "border-2 border-brand lg:-translate-y-2 shadow-lg" : "border-border"}`}>
                  {t.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand px-3 py-1 text-[10px] font-extrabold tracking-wide text-white">MOST POPULAR</span>}
                  <p className={`text-[13px] font-extrabold tracking-widest ${t.highlight ? "text-brand" : "text-slate-500"}`}>{t.name}</p>
                  <div className="mt-2 flex items-end gap-1">
                    <span className="mb-1.5 text-2xl font-bold text-midnight">£</span>
                    <span data-testid={`tier-price-${t.id}`} className="font-display text-6xl font-bold leading-none text-midnight">{price}</span>
                    <span className="mb-2 text-base font-semibold text-slate-500">{period === "annual" ? "/yr" : "/mo"}</span>
                  </div>
                  {period === "annual" && <p data-testid={`tier-equiv-${t.id}`} className="mt-0.5 text-[11px] font-bold text-brand-2">≈ £{(annualTotal / 12).toFixed(2).replace(/\.00$/, "")}/mo · billed yearly</p>}
                  <p className="mt-1 text-xs italic text-slate-500">{t.tagline}</p>
                  <ul className="mt-5 space-y-2">
                    {t.includes.map((l) => <li key={l} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{l}</li>)}
                    {t.excludes.map((l) => <li key={l} className="flex items-start gap-2 text-sm text-slate-400"><X className="mt-0.5 h-4 w-4 shrink-0" />{l}</li>)}
                  </ul>
                  <button onClick={() => pickTier(t.id)} data-testid={`tier-cta-${t.id}`}
                    className={`mt-6 w-full rounded-lg border py-3 text-sm font-extrabold tracking-wide transition-transform hover:scale-[1.02] ${t.highlight ? "border-brand bg-brand text-white" : "border-brand bg-background text-brand"}`}>
                    {t.highlight ? "START FREE TRIAL" : `CHOOSE ${t.name}`}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-xs text-slate-500">14-day free trial · Payment details required to start your trial · Cancel anytime, subject to the applicable cancellation terms.</p>
        </div>
      </section>

      {/* Compare plans */}
      <section className="px-6 py-20 md:px-10" data-testid="compare-section">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <Eyebrow>COMPARE PLANS</Eyebrow>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-midnight sm:text-4xl">Every feature, side by side</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600">See exactly what is included in each plan so you can pick the right fit for your workshop.</p>
          </div>
          <div className="mt-10 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[640px] border-collapse bg-background text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-4 text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Feature</th>
                  <th className="px-4 py-4 text-center">
                    <span className="block font-display text-base font-bold uppercase text-midnight">Basic</span>
                    <span className="text-[11px] font-semibold text-slate-500">£40/mo</span>
                  </th>
                  <th className="bg-brand-tertiary px-4 pt-4 pb-4 text-center">
                    <span className="mb-1 inline-block rounded-full bg-brand px-2.5 py-0.5 text-[9px] font-extrabold tracking-wide text-white">MOST POPULAR</span>
                    <span className="block font-display text-base font-bold uppercase text-brand-2">Pro</span>
                    <span className="text-[11px] font-semibold text-brand-2/80">£50/mo</span>
                  </th>
                  <th className="px-4 py-4 text-center">
                    <span className="block font-display text-base font-bold uppercase text-midnight">Premium</span>
                    <span className="text-[11px] font-semibold text-slate-500">£60/mo</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={row.label} data-testid={`compare-row-${i}`} className={i % 2 ? "bg-secondary/40" : ""}>
                    <td className="px-4 py-3 text-sm font-semibold text-midnight">{row.label}</td>
                    <CompareCell v={row.basic} />
                    <CompareCell v={row.pro} highlight />
                    <CompareCell v={row.premium} />
                  </tr>
                ))}
                <tr className="border-t border-border">
                  <td className="px-4 py-4" />
                  <td className="px-4 py-4 text-center"><button onClick={() => pickTier("basic")} data-testid="compare-cta-basic" className="rounded-lg border border-brand px-4 py-2 text-xs font-extrabold tracking-wide text-brand transition-transform hover:scale-105">CHOOSE BASIC</button></td>
                  <td className="bg-brand-tertiary px-4 py-4 text-center"><button onClick={() => pickTier("pro")} data-testid="compare-cta-pro" className="rounded-lg bg-brand px-4 py-2 text-xs font-extrabold tracking-wide text-white transition-transform hover:scale-105">START FREE TRIAL</button></td>
                  <td className="px-4 py-4 text-center"><button onClick={() => pickTier("premium")} data-testid="compare-cta-premium" className="rounded-lg border border-brand px-4 py-2 text-xs font-extrabold tracking-wide text-brand transition-transform hover:scale-105">CHOOSE PREMIUM</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">All plans include a 14-day free trial. Payment details are required to start your trial.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-20 md:px-10">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-display text-3xl font-bold text-midnight sm:text-4xl">Questions, answered</h2>
          </div>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-base font-bold uppercase text-midnight">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-midnight-2 to-midnight px-6 py-24 text-center md:px-10">
        <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-white sm:text-4xl">Ready to run your workshop from one screen?</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/75">Explore the system, see how the features fit together, and start your 14-day free trial today.</p>
        <button onClick={startTrial} data-testid="final-cta" className="mx-auto mt-8 inline-flex items-center gap-2 rounded-lg bg-brand px-8 py-4 text-sm font-extrabold tracking-wide text-white transition-transform hover:scale-105">
          START YOUR 14-DAY FREE TRIAL <ArrowRight className="h-4 w-4" />
        </button>
        <p className="mt-4 text-xs text-white/55">Payment details are required to start your trial. Cancellation is subject to the applicable terms. Questions? Email support@progarages.co.uk</p>
      </section>

      <Footer />
    </div>
  );
}
